import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  cancelSubscription,
  createSubscriptionCheckoutSession,
  createSubscriptionPortalSession,
  fetchSubscriptionStatus,
  reactivateSubscription,
  reportSubscriptionCheckoutReturn,
  updateSubscription,
} from '../../util/api';
import { storableError } from '../../util/errors';
import { fetchCurrentUser } from '../../ducks/user.duck';

const fetchCurrentUserOptions = {
  updateHasListings: false,
  updateNotifications: false,
  enforce: true,
};

export const fetchSubscriptionStatusThunk = createAsyncThunk(
  'SubscriptionSettingsPage/fetchSubscriptionStatus',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await fetchSubscriptionStatus();
      await dispatch(fetchCurrentUser(fetchCurrentUserOptions));
      return response;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const startSubscriptionCheckoutThunk = createAsyncThunk(
  'SubscriptionSettingsPage/startSubscriptionCheckout',
  async ({ planId }, { rejectWithValue }) => {
    try {
      return await createSubscriptionCheckoutSession({ planId });
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const createBillingPortalSessionThunk = createAsyncThunk(
  'SubscriptionSettingsPage/createBillingPortalSession',
  async (_, { rejectWithValue }) => {
    try {
      return await createSubscriptionPortalSession();
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const reportCheckoutReturnThunk = createAsyncThunk(
  'SubscriptionSettingsPage/reportCheckoutReturn',
  async ({ checkoutStatus, sessionId }, { rejectWithValue }) => {
    try {
      return await reportSubscriptionCheckoutReturn({ checkoutStatus, sessionId });
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const cancelSubscriptionThunk = createAsyncThunk(
  'SubscriptionSettingsPage/cancelSubscription',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await cancelSubscription();
      await dispatch(fetchCurrentUser(fetchCurrentUserOptions));
      return response;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const reactivateSubscriptionThunk = createAsyncThunk(
  'SubscriptionSettingsPage/reactivateSubscription',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const response = await reactivateSubscription();
      await dispatch(fetchCurrentUser(fetchCurrentUserOptions));
      return response;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const updateSubscriptionThunk = createAsyncThunk(
  'SubscriptionSettingsPage/updateSubscription',
  async ({ planId }, { dispatch, rejectWithValue }) => {
    try {
      const response = await updateSubscription({ planId });
      await dispatch(fetchCurrentUser(fetchCurrentUserOptions));
      return response;
    } catch (e) {
      return rejectWithValue(storableError(e));
    }
  }
);

export const fetchSubscriptionPageData = () => dispatch => {
  return dispatch(fetchSubscriptionStatusThunk()).unwrap();
};

export const startSubscriptionCheckout = planId => dispatch => {
  return dispatch(startSubscriptionCheckoutThunk({ planId })).unwrap();
};

export const createBillingPortalSession = () => dispatch => {
  return dispatch(createBillingPortalSessionThunk()).unwrap();
};

export const reportCheckoutReturn = ({ checkoutStatus, sessionId }) => dispatch => {
  return dispatch(reportCheckoutReturnThunk({ checkoutStatus, sessionId })).unwrap();
};

export const requestSubscriptionCancel = () => dispatch => {
  return dispatch(cancelSubscriptionThunk()).unwrap();
};

export const requestSubscriptionReactivate = () => dispatch => {
  return dispatch(reactivateSubscriptionThunk()).unwrap();
};

export const requestSubscriptionUpdate = planId => dispatch => {
  return dispatch(updateSubscriptionThunk({ planId })).unwrap();
};

const subscriptionSettingsPageSlice = createSlice({
  name: 'SubscriptionSettingsPage',
  initialState: {
    fetchSubscriptionStatusError: null,
    fetchSubscriptionStatusInProgress: false,
    subscription: null,
    plans: [],
    transactions: [],
    checkoutInProgress: false,
    checkoutError: null,
    portalInProgress: false,
    portalError: null,
    cancelInProgress: false,
    cancelError: null,
    reactivateInProgress: false,
    reactivateError: null,
    updateInProgress: false,
    updateError: null,
    updateSuccess: false,
    selectedCheckoutPlanId: null,
  },
  reducers: {
    resetSuccess: state => {
      state.updateSuccess = false;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchSubscriptionStatusThunk.pending, state => {
        state.fetchSubscriptionStatusInProgress = true;
        state.fetchSubscriptionStatusError = null;
      })
      .addCase(fetchSubscriptionStatusThunk.fulfilled, (state, action) => {
        state.fetchSubscriptionStatusInProgress = false;
        state.fetchSubscriptionStatusError = null;
        state.subscription = action.payload.subscription;
        state.plans = action.payload.plans || [];
        state.transactions = action.payload.transactions || [];
      })
      .addCase(fetchSubscriptionStatusThunk.rejected, (state, action) => {
        state.fetchSubscriptionStatusInProgress = false;
        state.fetchSubscriptionStatusError = action.payload;
      })
      .addCase(startSubscriptionCheckoutThunk.pending, (state, action) => {
        state.checkoutInProgress = true;
        state.checkoutError = null;
        state.selectedCheckoutPlanId = action.meta.arg.planId;
      })
      .addCase(startSubscriptionCheckoutThunk.fulfilled, state => {
        state.checkoutInProgress = false;
        state.selectedCheckoutPlanId = null;
      })
      .addCase(startSubscriptionCheckoutThunk.rejected, (state, action) => {
        state.checkoutInProgress = false;
        state.checkoutError = action.payload;
        state.selectedCheckoutPlanId = null;
      })
      .addCase(reportCheckoutReturnThunk.rejected, (state, action) => {
        state.checkoutError = action.payload;
      })
      .addCase(createBillingPortalSessionThunk.pending, state => {
        state.portalInProgress = true;
        state.portalError = null;
      })
      .addCase(createBillingPortalSessionThunk.fulfilled, state => {
        state.portalInProgress = false;
      })
      .addCase(createBillingPortalSessionThunk.rejected, (state, action) => {
        state.portalInProgress = false;
        state.portalError = action.payload;
      })
      .addCase(cancelSubscriptionThunk.pending, state => {
        state.cancelInProgress = true;
        state.cancelError = null;
      })
      .addCase(cancelSubscriptionThunk.fulfilled, (state, action) => {
        state.cancelInProgress = false;
        state.subscription = action.payload.subscription;
      })
      .addCase(cancelSubscriptionThunk.rejected, (state, action) => {
        state.cancelInProgress = false;
        state.cancelError = action.payload;
      })
      .addCase(reactivateSubscriptionThunk.pending, state => {
        state.reactivateInProgress = true;
        state.reactivateError = null;
      })
      .addCase(reactivateSubscriptionThunk.fulfilled, (state, action) => {
        state.reactivateInProgress = false;
        state.subscription = action.payload.subscription;
      })
      .addCase(reactivateSubscriptionThunk.rejected, (state, action) => {
        state.reactivateInProgress = false;
        state.reactivateError = action.payload;
      })
      .addCase(updateSubscriptionThunk.pending, state => {
        state.updateInProgress = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updateSubscriptionThunk.fulfilled, (state, action) => {
        state.updateInProgress = false;
        state.updateSuccess = true;
        state.subscription = action.payload.subscription;
      })
      .addCase(updateSubscriptionThunk.rejected, (state, action) => {
        state.updateInProgress = false;
        state.updateError = action.payload;
        state.updateSuccess = false;
      });
  },
});

export const { resetSuccess } = subscriptionSettingsPageSlice.actions;

export default subscriptionSettingsPageSlice.reducer;

export const loadData = () => dispatch => {
  return dispatch(fetchCurrentUser(fetchCurrentUserOptions));
};
